import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { TextFormatButtons, TokenInsertButtons, appendToken } from './tokenTools';
import IconClassPicker from './IconClassPicker';

export default function InteractiveEditor({ type, content, update }) {
  const [activeTarget, setActiveTarget] = useState(null);

  const insertToken = (token) => {
    if (activeTarget === 'countdown:title') {
      update('title', appendToken(content.title || '', token));
      return;
    }

    if (activeTarget === 'countdown:completedMessage') {
      update('completedMessage', appendToken(content.completedMessage || '', token));
      return;
    }

    if (activeTarget === 'poll:question') {
      update('question', appendToken(content.question || '', token));
      return;
    }

    if (activeTarget?.startsWith('poll:option:')) {
      const optionId = activeTarget.split(':')[2];
      const options = content.options || [];
      update('options', options.map((option) => (option.id === optionId ? { ...option, text: appendToken(option.text || '', token) } : option)));
      return;
    }

    if (activeTarget === 'announcement:text') {
      update('text', appendToken(content.text || '', token));
    }
  };

  switch (type) {

    case 'countdown':
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              value={content.title || ''}
              onChange={(e) => update('title', e.target.value)}
              onFocus={() => setActiveTarget('countdown:title')}
              placeholder="Countdown title…"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Target date &amp; time</Form.Label>
            <Form.Control
              type="datetime-local"
              value={content.targetDate || ''}
              onChange={(e) => update('targetDate', e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Message when complete</Form.Label>
            <Form.Control
              value={content.completedMessage || ''}
              onChange={(e) => update('completedMessage', e.target.value)}
              onFocus={() => setActiveTarget('countdown:completedMessage')}
              placeholder="Time is up!"
            />
          </Form.Group>
          <IconClassPicker
            value={content.icon || ''}
            label="Countdown Icon (optional)"
            placeholder="Search countdown icon..."
            maxRows={2}
            onChange={(nextIconClass) => update('icon', nextIconClass)}
          />
          <TokenInsertButtons onInsert={insertToken} />
        </div>
      );

    case 'poll': {
      const options = content.options || [];
      const addOption = () => update('options', [...options, { id: `opt-${Date.now()}`, text: '' }]);
      const removeOption = (oid) => update('options', options.filter((o) => o.id !== oid));
      const updateOption = (oid, val) => update('options', options.map((o) => o.id === oid ? { ...o, text: val } : o));
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <Form.Group className="mb-3">
            <Form.Label>Question</Form.Label>
            <Form.Control
              value={content.question || ''}
              onChange={(e) => update('question', e.target.value)}
              onFocus={() => setActiveTarget('poll:question')}
              placeholder="What would you like to ask?"
            />
          </Form.Group>
          <div className="pagebuilder-sublist-header">
            <span className="pagebuilder-sublist-title">Options ({options.length})</span>
            <button type="button" className="pagebuilder-add-btn pagebuilder-add-btn--sm" onClick={addOption}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> Add Option
            </button>
          </div>
          {options.length === 0 && <p className="pagebuilder-sublist-empty">No options yet.</p>}
          {options.map((opt, idx) => (
            <div key={opt.id} className="pagebuilder-sublist-item pagebuilder-sublist-item--row">
              <Form.Control
                style={{ flex: 1 }}
                value={opt.text}
                onChange={(e) => updateOption(opt.id, e.target.value)}
                onFocus={() => setActiveTarget(`poll:option:${opt.id}`)}
                placeholder={`Option ${idx + 1}…`}
              />
              <button type="button" className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger" onClick={() => removeOption(opt.id)} title="Remove">
                <i className="bi bi-trash" />
              </button>
            </div>
          ))}
          <Form.Check
            className="mt-3"
            type="checkbox"
            label="Allow selecting multiple options"
            checked={!!content.allowMultiple}
            onChange={(e) => update('allowMultiple', e.target.checked)}
          />
          <TokenInsertButtons onInsert={insertToken} />
        </div>
      );
    }

    case 'announcement':
      return (
        <div className="pagebuilder-editor-fields">
          <TextFormatButtons />
          <Form.Group className="mb-3">
            <Form.Label>Variant</Form.Label>
            <Form.Select value={content.variant || 'info'} onChange={(e) => update('variant', e.target.value)}>
              <option value="info">ℹ️ Info</option>
              <option value="warning">⚠️ Warning</option>
              <option value="success">✅ Success</option>
              <option value="danger">🚨 Danger</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={content.text || ''}
              onChange={(e) => update('text', e.target.value)}
              onFocus={() => setActiveTarget('announcement:text')}
              placeholder="Announcement message…"
            />
          </Form.Group>
          <Form.Check
            type="checkbox"
            label="Show dismiss button"
            checked={content.dismissible !== false}
            onChange={(e) => update('dismissible', e.target.checked)}
          />
          <IconClassPicker
            value={content.icon || ''}
            label="Announcement Icon (optional)"
            placeholder="Search announcement icon..."
            maxRows={2}
            onChange={(nextIconClass) => update('icon', nextIconClass)}
          />
          <TokenInsertButtons onInsert={insertToken} />
        </div>
      );

    default:
      return null;
  }
}
